"use client"

import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import { User, Shield, Trophy, ChevronRight, AlertCircle, CheckCircle2, FileText, Camera } from "lucide-react"
import { CircuitoContext } from "../../context/CircuitoContext"
import { UserContext } from "../../context/UserContext"
import "../../styles/circuitoDesignSystem.css"
import { DomainContext } from "./LayoutCircuito"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function CircuitoInscricaoDados() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { circuito, loadingCircuito } = useContext(CircuitoContext)
  const { user, token: tokenCtx, isLoading: authLoading } = useContext(UserContext)
  const { basePath } = useContext(DomainContext)
  
  const [nomeVaqueiro, setNomeVaqueiro] = useState("")
  const [cpfVaqueiro, setCpfVaqueiro] = useState("")
  const [equipe, setEquipe] = useState("")
  const [categoria, setCategoria] = useState("")
  
  const [querCavalo, setQuerCavalo] = useState(false)
  const [cavalo, setCavalo] = useState("")

  // 💡 MODO DE INSCRIÇÃO: "vaqueiro" (vaqueiro, ou vaqueiro+cavalo) ou "cavalo_avulso" (só cavalo)
  const [modo, setModo] = useState("vaqueiro")
  // Campos do cavalo avulso
  const [cavaloAvulso, setCavaloAvulso] = useState("")
  const [haras, setHaras] = useState("")
  const [responsavel, setResponsavel] = useState("")

  // 💡 ESTADO DA FOTO MOVIDO PARA O TOPO (CORRETO)
  const [fotoVaqueiro, setFotoVaqueiro] = useState(null)

  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState("")

  useEffect(() => {
    // Aguarda o UserContext terminar de inicializar antes de redirecionar.
    // Sem isso, em domínios exclusivos o user ainda é null durante o carregamento
    // e o usuário é redirecionado pro login mesmo estando logado.
    if (!authLoading && !user) {
      navigate(`${basePath}/inscrever-se/acesso`)
    }
  }, [user, authLoading, slug, navigate])

  const handleCpfChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); 
    if (value.length > 11) value = value.slice(0, 11);
    
    if (value.length > 9) value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    else if (value.length > 6) value = value.replace(/(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
    else if (value.length > 3) value = value.replace(/(\d{3})(\d{1,3})/, "$1.$2");
    
    setCpfVaqueiro(value);
  }

  // 💡 FUNÇÃO DA FOTO MOVIDA PARA FORA DO handleContinuar (CORRETO)
  const handleFotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => setFotoVaqueiro(event.target.result)
      reader.readAsDataURL(file)
    }
  }

  const loteAtualDaCategoria = (cat) => {
    if (cat?.dataViradaLote && Number(cat?.valorLote2) > 0) {
      const dataVirada = new Date(cat.dataViradaLote)
      if (!Number.isNaN(dataVirada.getTime()) && new Date() > dataVirada) return 2
    }
    return 1
  }

  const precoCavaloVinculadoDaCategoria = (cat) => {
    if (!cat) return 0
    if (loteAtualDaCategoria(cat) === 2 && Number(cat.valorCavaloLote2) > 0) {
      return Number(cat.valorCavaloLote2) || 0
    }
    return Number(cat.valorCavalo || 0)
  }

  const handleContinuar = async () => {
    // ── MODO CAVALO AVULSO ──────────────────────────────────────────────
    if (modo === "cavalo_avulso") {
      if (!cavaloAvulso.trim() || !haras.trim() || !responsavel.trim() || !categoria) {
        setErro("Preencha nome do cavalo, haras, responsável e selecione a categoria.")
        return
      }
      try {
        setLoading(true)
        setErro("")
        const token = tokenCtx || localStorage.getItem("token")
        const afiliadoCodigo = localStorage.getItem("afiliado_ref") || ""

        const res = await axios.post(
          `${API}/circuitos/${circuito._id}/inscricoes-cavalo`,
          {
            cavalo: cavaloAvulso,
            haras,
            responsavel,
            categoria,
            afiliadoCodigo,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )

        navigate(`${basePath}/inscrever-se/pagamento`, {
          state: {
            pedidoId: res.data.pedidoId,
            total: res.data.total,
            nomeEvento: `Inscrição Cavalo - ${circuito.nome}`,
            categoriaSelecionada: categoria,
            tipoInscricao: res.data.tipoInscricao || "cavalo_avulso",
          },
        })
      } catch (e) {
        setErro(e?.response?.data?.erro || "Não foi possível cadastrar o cavalo. Tente novamente.")
      } finally {
        setLoading(false)
      }
      return
    }

    // ── MODO VAQUEIRO (padrão) ──────────────────────────────────────────
    if (!nomeVaqueiro || !cpfVaqueiro || !equipe || !categoria) {
      setErro("Preencha todos os campos para continuar.")
      return
    }

    if (cpfVaqueiro.replace(/\D/g, "").length !== 11) {
      setErro("Digite um CPF válido para o vaqueiro.")
      return
    }

    if (querCavalo && !cavalo.trim()) {
      setErro("Por favor, informe o nome do cavalo que deseja cadastrar.")
      return
    }

    try {
      setLoading(true)
      setErro("")
      const token = tokenCtx || localStorage.getItem("token")
      const afiliadoCodigo = localStorage.getItem("afiliado_ref") || "";

      const res = await axios.post(
        `${API}/circuitos/${circuito._id}/inscricoes`,
        {
          nomeVaqueiro,
          cpfVaqueiro, 
          fotoVaqueiro,
          equipe,
          categoria,
          cavalo: querCavalo ? cavalo : "", 
          afiliadoCodigo,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      navigate(`${basePath}/inscrever-se/pagamento`, {
        state: {
          pedidoId: res.data.pedidoId,
          total: res.data.total, 
          nomeEvento: `Inscrição - ${circuito.nome}`,
          categoriaSelecionada: categoria,
            tipoInscricao: res.data.tipoInscricao || "vaqueiro",
        },
      })
    } catch (e) {
      setErro(e?.response?.data?.erro || "Não foi possível criar a inscrição. Tente novamente.")
    } finally {
      setLoading(false)
    }
  }

  if (loadingCircuito || !circuito) {
    return (
      <div className="circuito-shell">
        <div className="c-loading" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    )
  }

  const catSelecionadaObj = circuito.categorias?.find(c => (c.nome || c) === categoria);
  const loteAtualCatSelecionada = loteAtualDaCategoria(catSelecionadaObj);
  const precoCavalo = precoCavaloVinculadoDaCategoria(catSelecionadaObj);
  const precoCavaloAvulso = catSelecionadaObj?.valorCavaloAvulso || 0;

  return (
    <div className="circuito-shell">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ maxWidth: 580, margin: "0 auto", padding: "40px 16px" }}
      >
        <div
          style={{
            background: "var(--c-card)",
            borderRadius: "var(--card-radius)",
            padding: "40px 32px",
            border: "var(--card-border)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h1 style={{ fontSize: "28px", fontWeight: 800, marginBottom: 8 }}>
              {modo === "cavalo_avulso" ? (
                <>Cadastro do <span style={{ color: "var(--c-accent)" }}>Cavalo</span></>
              ) : (
                <>Dados da <span style={{ color: "var(--c-accent)" }}>Associação</span></>
              )}
            </h1>
            <p style={{ opacity: 0.7, fontSize: "15px" }}>
              {modo === "cavalo_avulso"
                ? "Cadastre somente o cavalo. Esta opção não cria associação de vaqueiro."
                : "Informe seus dados para concluir a associação ao circuito."}
            </p>
          </div>

          {erro && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                marginBottom: 24, padding: "14px 16px", borderRadius: 12,
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)",
                color: "#f87171", fontSize: "14px", display: "flex", alignItems: "center", gap: 10
              }}
            >
              <AlertCircle size={20} />
              <span>{erro}</span>
            </motion.div>
          )}

          {/* 💡 SELETOR DE MODO: Vaqueiro vs Só Cavalo */}
          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            {[
              { key: "vaqueiro", label: "Inscrever Vaqueiro" },
              { key: "cavalo_avulso", label: "Só Cavalo (avulso)" },
            ].map((opt) => {
              const ativo = modo === opt.key
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                      setModo(opt.key)
                      setErro("")
                      // Não envia dados de vaqueiro ao escolher o cadastro somente do cavalo.
                      if (opt.key === "cavalo_avulso") {
                        setQuerCavalo(false)
                        setCavalo("")
                      }
                    }}
                  style={{
                    flex: 1, padding: "14px 12px", borderRadius: 12, cursor: "pointer",
                    fontWeight: 700, fontSize: 14,
                    border: ativo ? "2px solid var(--c-accent)" : "1px solid rgba(255,255,255,0.12)",
                    background: ativo ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)",
                    color: ativo ? "var(--c-accent)" : "rgba(255,255,255,0.6)",
                    transition: "all 0.2s ease",
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {modo === "vaqueiro" && (
            <>
            <div>
              <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Nome do Vaqueiro</label>
              <div style={{ position: "relative" }}>
                <User size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
                <input
                  type="text"
                  placeholder="Ex: João Silva"
                  value={nomeVaqueiro}
                  onChange={(e) => setNomeVaqueiro(e.target.value)}
                  className="c-input"
                  style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>CPF do Vaqueiro</label>
              <div style={{ position: "relative" }}>
                <FileText size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
                <input
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpfVaqueiro}
                  onChange={handleCpfChange}
                  className="c-input"
                  style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
                />
              </div>
              <small style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", marginTop: "4px", display: "block" }}>
                Este CPF será registrado na associação, independente de quem está pagando.
              </small>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Equipe / Haras de Representação</label>
              <div style={{ position: "relative" }}>
                <Shield size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
                <input
                  type="text"
                  placeholder="Ex: Haras WS"
                  value={equipe}
                  onChange={(e) => setEquipe(e.target.value)}
                  className="c-input"
                  style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
                />
              </div>
            </div>
            
            <div>
              <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>
                Foto para a Carteirinha
              </label>
              <label style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", 
                border: "2px dashed rgba(255,255,255,0.2)", borderRadius: 12, padding: "20px 10px", 
                cursor: "pointer", background: "rgba(0,0,0,0.2)", transition: "0.3s"
              }}>
                <Camera size={30} color="rgba(255,255,255,0.4)" style={{ marginBottom: 10 }} />
                <span style={{ color: "#fff", fontSize: 14 }}>
                  {fotoVaqueiro ? "Foto selecionada! Clique para trocar." : "Toque para escolher uma foto de perfil"}
                </span>
                <input type="file" accept="image/jpeg, image/png, image/webp" onChange={handleFotoChange} style={{ display: "none" }} />
              </label>
            </div>
            </>
            )}

            {/* 💡 CAMPOS DO CAVALO AVULSO (só aparecem no modo cavalo_avulso) */}
            {modo === "cavalo_avulso" && (
            <>
              <div style={{ padding: "14px 16px", borderRadius: 12, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", fontSize: 13, color: "#f59e0b" }}>
                Cadastro de cavalo sem vaqueiro. O cavalo pontua na categoria selecionada como Cavalo de Puxada.
              </div>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Nome do Cavalo</label>
                <input type="text" placeholder="Ex: Holland Jeck" value={cavaloAvulso} onChange={(e) => setCavaloAvulso(e.target.value)} className="c-input" style={{ width: "100%", height: "48px", paddingLeft: "16px" }} />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Haras</label>
                <input type="text" placeholder="Ex: Haras São Francisco" value={haras} onChange={(e) => setHaras(e.target.value)} className="c-input" style={{ width: "100%", height: "48px", paddingLeft: "16px" }} />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Nome do Responsável</label>
                <input type="text" placeholder="Ex: João Silva" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} className="c-input" style={{ width: "100%", height: "48px", paddingLeft: "16px" }} />
              </div>
            </>
            )}

            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", marginBottom: 16, fontSize: "14px", fontWeight: 700, color: "var(--c-accent)", textTransform: "uppercase", letterSpacing: "1px" }}>
                Selecione sua Categoria
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                {circuito.categorias.map((c, index) => {
                  const catName = c.nome || c; 
                  const isSelected = categoria === catName;
                  
                  let catPrice = c.valor !== undefined ? Number(c.valor) : Number(circuito.taxaInscricao || 0);
                  let loteAtual = "1º Lote";
                  let mostraAvisoVirada = false;
                  let dataFormatada = "";
                  
                  if (c.dataViradaLote && Number(c.valorLote2) > 0) {
                    const agora = new Date();
                    const dataVirada = new Date(c.dataViradaLote);
                    
                    if (agora > dataVirada) {
                      catPrice = Number(c.valorLote2);
                      loteAtual = "2º Lote";
                    } else {
                      mostraAvisoVirada = true; 
                      dataFormatada = dataVirada.toLocaleString('pt-BR', { 
                        day: '2-digit', month: '2-digit', 
                        hour: '2-digit', minute:'2-digit' 
                      }).replace(',', ' às');
                    }
                  }

                  // 💡 No modo cavalo avulso, mostra o valor do cavalo avulso da categoria.
                  // No modo vaqueiro, a taxa do cavalo vinculado aparece no checkbox abaixo,
                  // usando valorCavalo no 1º lote e valorCavaloLote2 no 2º lote.
                  if (modo === "cavalo_avulso") {
                    catPrice = Number(c.valorCavaloAvulso || 0);
                  }

                  return (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setCategoria(catName)}
                      style={{
                        padding: "16px", borderRadius: "12px", cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                        textAlign: "center", border: isSelected ? "2px solid var(--c-accent)" : "1px solid rgba(255,255,255,0.1)",
                        background: isSelected ? "rgba(255, 255, 255, 0.05)" : "rgba(255,255,255,0.02)",
                        boxShadow: isSelected ? "0 8px 20px rgba(0,0,0,0.2)" : "none",
                        transition: "all 0.2s ease", position: "relative", overflow: "hidden"
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                          background: "radial-gradient(circle at center, var(--c-accent) 0%, transparent 80%)",
                          opacity: 0.1, pointerEvents: "none"
                        }} />
                      )}

                      {(c.dataViradaLote) && (
                        <span style={{
                          background: loteAtual === "1º Lote" ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                          color: loteAtual === "1º Lote" ? "#10b981" : "#f59e0b",
                          border: loteAtual === "1º Lote" ? "1px solid rgba(16, 185, 129, 0.4)" : "1px solid rgba(245, 158, 11, 0.4)",
                          fontSize: "10px", fontWeight: "800", padding: "4px 10px", borderRadius: "12px",
                          marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px"
                        }}>
                          {loteAtual}
                        </span>
                      )}

                      <Trophy size={26} color={isSelected ? "var(--c-accent)" : "rgba(255,255,255,0.3)"} strokeWidth={1.5} />
                      <span style={{ fontSize: "14px", fontWeight: isSelected ? 700 : 500, color: isSelected ? "var(--c-accent)" : "rgba(255,255,255,0.7)" }}>
                        {catName}
                      </span>
                      
                      <span style={{ fontSize: "16px", fontWeight: 800, color: isSelected ? "#fff" : "rgba(255,255,255,0.5)" }}>
                        R$ {Number(catPrice).toFixed(2)}
                      </span>

                      {mostraAvisoVirada && (
                        <small style={{ fontSize: "11px", color: "#f87171", marginTop: "4px", fontWeight: "600" }}>
                          Vira {dataFormatada}
                        </small>
                      )}

                      {isSelected && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ position: "absolute", top: 8, right: 8 }}>
                          <CheckCircle2 size={16} color="var(--c-accent)" />
                        </motion.div>
                      )}
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {categoria && modo === "vaqueiro" && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ marginTop: 8, padding: 20, borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <input 
                    type="checkbox" 
                    checked={querCavalo} 
                    onChange={(e) => setQuerCavalo(e.target.checked)}
                    style={{ width: 20, height: 20, accentColor: "var(--c-accent)", cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "15px", fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
                    Deseja cadastrar um cavalo para pontuar?
                    {precoCavalo > 0 && (
                      <span style={{ color: "var(--c-accent)", display: "block", fontSize: "13px", marginTop: "2px", fontWeight: "bold" }}>
                        + R$ {Number(precoCavalo).toFixed(2)} {loteAtualCatSelecionada === 2 ? "(Taxa extra do cavalo - 2º lote)" : "(Taxa extra do cavalo - 1º lote)"}
                      </span>
                    )}
                  </span>
                </label>

                <AnimatePresence>
                  {querCavalo && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }} 
                      animate={{ opacity: 1, height: "auto" }} 
                      exit={{ opacity: 0, height: 0 }}
                      style={{ marginTop: 16, overflow: "hidden" }}
                    >
                      <label style={{ display: "block", marginBottom: 8, fontSize: "14px", opacity: 0.8, fontWeight: 500 }}>Nome do Cavalo</label>
                      <input
                        type="text"
                        placeholder="Ex: Holland Jeck"
                        value={cavalo}
                        onChange={(e) => setCavalo(e.target.value)}
                        className="c-input"
                        style={{ width: "100%", height: "48px", paddingLeft: "16px" }}
                      />
                      <small style={{ display: "block", marginTop: 6, color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>
                        O valor da taxa do cavalo será adicionado automaticamente ao pagamento.
                      </small>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleContinuar}
            disabled={loading}
            className="c-btn c-btn--primary"
            style={{ 
              width: "100%", marginTop: 40, height: "56px", fontSize: "16px", fontWeight: 700,
              display: "flex", justifyContent: "center", alignItems: "center", gap: 8
            }}
          >
            {loading ? <span style={{ opacity: 0.8 }}>Processando...</span> : <>Ir para Pagamento <ChevronRight size={20} /></>}
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}